/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { VersionHelper } from '../../shared/helpers/version.helper';
import { SetGlobalLmsLaunch } from '../store/user-auth.actions';
import { ORGANIZATION_WAIT_MS, UserAuthState } from '../store/user-auth.state';

/** `info` rather than `state`, which the router restores with the history entry on a reload or Back. */
const LANDING_ON_MY_HUB: NavigationExtras = { replaceUrl: true, info: 'landing-on-my-hub' };

export class GlobalLaunchHelper {
  /**
   * Where a launch without a course lands: an LTI 1.3 global placement (`destination=folio` or `skills`) or the Moodle
   * plugin's profile launch (`target=PROFILE`). MyHub on V3 orgs with `lti_my_hub`, otherwise the profile on V3
   * and the folio before it.
   *
   * On a fresh launch the organisation loads alongside the sign-in, so this waits for it. The V3 check reads the
   * organisation rather than `VersionHelper`: the localStorage flag behind it is only set once `MainComponent`
   * has handled the same load, which can come after this.
   *
   * A failed load lands where `MainComponent` then marks the app as: V2 and the folio, unless the environment forces
   * V3. A load that never finishes goes by the flag of the last visit, as nothing has overwritten it.
   */
  static destination(store: Store): Observable<string> {
    return UserAuthState.organizationSettled$(store).pipe(
      map(({ data }) => {
        if (!environment.isV3Enabled && data?.ui !== 'v3') {
          return '/folio';
        }
        return store.selectSnapshot(UserAuthState.ltiMyHubFeatureFlagEnabled) ? '/my-hub' : '/profile';
      }),
      timeout({ first: ORGANIZATION_WAIT_MS, with: () => of(VersionHelper.isV3Enabled() ? '/profile' : '/folio') }),
      tap((path) => store.dispatch(new SetGlobalLmsLaunch(path === '/my-hub'))),
    );
  }

  /** The launch's one-time address leaves the frame's history, or Back from MyHub would replay it and end the session. */
  static redirectExtras(path: string): NavigationExtras | undefined {
    return path === '/my-hub' ? LANDING_ON_MY_HUB : undefined;
  }

  /**
   * For a guard sending the current navigation elsewhere, so that a launch landing on MyHub still leaves its address out of
   * the history. Not `extras.replaceUrl`: the router sets that on every page load and every Back or Forward too.
   */
  static guardRedirectExtras(router: Router): NavigationExtras | undefined {
    return router.getCurrentNavigation()?.extras.info === LANDING_ON_MY_HUB.info ? LANDING_ON_MY_HUB : undefined;
  }
}
